import React, { useMemo, useState } from 'react';
import {
  Badge,
  Button,
  Card,
  Group,
  Pagination,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
} from '@mantine/core';
import { IconDownload, IconSearch } from '@tabler/icons-react';
import { QueryState } from './KpiPage';
import { toCsv, downloadCsv, stampedFilename } from './csvExport';
import useNexusQuery, { field } from '../../hooks/useNexusQuery';

const PAGE_SIZE = 25;

const COLUMNS = [
  { key: 'partnumber', label: 'Part Number' },
  { key: 'description', label: 'Description' },
  { key: 'cls', label: 'Class' },
  { key: 'reason', label: 'Reason' },
  { key: 'hasSellPrice', label: 'Has Sell Price' },
];

export default function ManagedPartsTable() {
  const list = useNexusQuery('initiative-managed-parts-list', undefined);

  const [search, setSearch] = useState('');
  const [classFilter, setClassFilter] = useState(null);
  const [reasonFilter, setReasonFilter] = useState(null);
  const [page, setPage] = useState(1);

  const rows = useMemo(
    () =>
      list.rows.map((r) => ({
        partnumber: field(r, 'Partnumber') ?? '',
        description: field(r, 'Description') ?? '',
        cls: field(r, 'Class') ?? '',
        reason: field(r, 'Reason') ?? '',
        hasSellPrice: field(r, 'HasSellPrice') ?? '',
      })),
    [list.rows]
  );

  const classOptions = useMemo(
    () => Array.from(new Set(rows.map((r) => r.cls))).filter(Boolean).sort(),
    [rows]
  );
  const reasonOptions = useMemo(
    () => Array.from(new Set(rows.map((r) => r.reason))).filter(Boolean).sort(),
    [rows]
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter(
      (r) =>
        (!classFilter || r.cls === classFilter) &&
        (!reasonFilter || r.reason === reasonFilter) &&
        (!q ||
          r.partnumber.toLowerCase().includes(q) ||
          r.description.toLowerCase().includes(q))
    );
  }, [rows, search, classFilter, reasonFilter]);

  // Any filter change can leave you past the end of the new result set.
  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount);
  const pageRows = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const resetPage = (fn) => (v) => {
    fn(v);
    setPage(1);
  };

  return (
    <Card withBorder radius="md" p="md">
      <Stack gap="sm">
        <Group justify="space-between" align="flex-start" wrap="nowrap">
          <Stack gap={2}>
            <Text fw={600}>Managed parts</Text>
            <Text size="xs" c="dimmed">
              Every part on the list, and why it qualifies. A part meeting both rules is reported as
              Class A/B — the deliberate designation takes priority over the incidental one.
            </Text>
          </Stack>
          <Button
            size="xs"
            variant="light"
            leftSection={<IconDownload size={14} />}
            disabled={!filtered.length}
            onClick={() =>
              downloadCsv(stampedFilename('managed-parts'), toCsv(filtered, COLUMNS))
            }
          >
            CSV ({filtered.length.toLocaleString()})
          </Button>
        </Group>

        <Group gap="sm" wrap="wrap">
          <TextInput
            placeholder="Search part number or description"
            leftSection={<IconSearch size={14} />}
            value={search}
            onChange={(e) => resetPage(setSearch)(e.currentTarget.value)}
            style={{ flex: 1, minWidth: 240 }}
            size="xs"
          />
          <Select
            placeholder="All classes"
            data={classOptions}
            value={classFilter}
            onChange={resetPage(setClassFilter)}
            clearable
            size="xs"
            w={140}
          />
          <Select
            placeholder="All reasons"
            data={reasonOptions}
            value={reasonFilter}
            onChange={resetPage(setReasonFilter)}
            clearable
            size="xs"
            w={180}
          />
        </Group>

        <QueryState isLoading={list.isLoading} error={list.error} isEmpty={!rows.length}>
          <>
            <Text size="xs" c="dimmed">
              {filtered.length.toLocaleString()} of {rows.length.toLocaleString()} parts
            </Text>

            <div style={{ overflowX: 'auto' }}>
              <Table striped highlightOnHover withTableBorder verticalSpacing="xs" fz="sm">
                <Table.Thead>
                  <Table.Tr>
                    {COLUMNS.map((c) => (
                      <Table.Th key={c.key}>{c.label}</Table.Th>
                    ))}
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {pageRows.map((r) => (
                    <Table.Tr key={`${r.partnumber}-${r.cls}`}>
                      <Table.Td ff="monospace">{r.partnumber}</Table.Td>
                      <Table.Td>{r.description}</Table.Td>
                      <Table.Td>{r.cls}</Table.Td>
                      <Table.Td>
                        <Badge
                          size="sm"
                          variant="light"
                          color={r.reason === 'Class A/B' ? 'blue' : 'grape'}
                        >
                          {r.reason}
                        </Badge>
                      </Table.Td>
                      <Table.Td>
                        <Badge
                          size="sm"
                          variant="light"
                          color={r.hasSellPrice === 'Y' ? 'teal' : 'gray'}
                        >
                          {r.hasSellPrice}
                        </Badge>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                  {!pageRows.length && (
                    <Table.Tr>
                      <Table.Td colSpan={COLUMNS.length}>
                        <Text size="sm" c="dimmed" ta="center" py="md">
                          No parts match those filters.
                        </Text>
                      </Table.Td>
                    </Table.Tr>
                  )}
                </Table.Tbody>
              </Table>
            </div>

            {pageCount > 1 && (
              <Group justify="center">
                <Pagination
                  value={safePage}
                  onChange={setPage}
                  total={pageCount}
                  size="sm"
                  withEdges
                />
              </Group>
            )}
          </>
        </QueryState>
      </Stack>
    </Card>
  );
}
